import React from 'react';

/**
 * KaziPeople brand mark — the "KP" monogram and optional wordmark.
 *
 * The monogram is a rounded-square tile (radius = 22% of size) carrying a white
 * "K" (vertical stem + two diagonals) fused with a "P" bowl arc. Variants change
 * the tile/stroke colors so the mark works on light and dark surfaces.
 */
type IconVariant = 'color' | 'charcoal' | 'white' | 'peach';

const ICON_CONFIGS: Record<IconVariant, { bg: string; fg: string }> = {
  color: { bg: 'linear-gradient(145deg,#F59040,#E27018)', fg: '#FFFFFF' },
  charcoal: { bg: '#2F3443', fg: '#FFFFFF' },
  white: { bg: '#FFFFFF', fg: '#2F3443' },
  peach: { bg: 'linear-gradient(145deg,#F6B26B,#F28A3A)', fg: '#FFFFFF' },
};

export function KaziPeopleIcon({
  size = 32,
  variant = 'color',
  className,
  style,
}: {
  size?: number;
  variant?: IconVariant;
  className?: string;
  style?: React.CSSProperties;
}) {
  const { bg, fg } = ICON_CONFIGS[variant];
  const sw = 8.8;
  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.22,
        background: bg,
        flexShrink: 0,
        overflow: 'hidden',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden="true">
        <line x1="50" y1="20" x2="50" y2="80" stroke={fg} strokeWidth={sw} strokeLinecap="round" />
        <line x1="50" y1="50" x2="27" y2="20" stroke={fg} strokeWidth={sw} strokeLinecap="round" />
        <line x1="50" y1="50" x2="27" y2="80" stroke={fg} strokeWidth={sw} strokeLinecap="round" />
        <path d="M 50 20 A 17 17 0 0 1 50 50" stroke={fg} strokeWidth={sw} strokeLinecap="round" fill="none" />
      </svg>
    </div>
  );
}

/**
 * Full lockup: monogram + "KaziPeople" wordmark. `wordmarkColor` sets the "Kazi"
 * color (charcoal on light surfaces, white on dark); "People" is always peach.
 */
export function KaziPeopleLogo({
  size = 32,
  variant = 'color',
  wordmark = true,
  wordmarkColor = '#2F3443',
  peachColor = '#F6B26B',
  className,
  style,
}: {
  size?: number;
  variant?: IconVariant;
  wordmark?: boolean;
  wordmarkColor?: string;
  peachColor?: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.34, ...style }}
    >
      <KaziPeopleIcon size={size} variant={variant} />
      {wordmark && (
        <span
          style={{
            fontWeight: 800,
            fontSize: size * 0.6,
            letterSpacing: '-0.01em',
            lineHeight: 1,
            color: wordmarkColor,
            whiteSpace: 'nowrap',
          }}
        >
          Kazi<span style={{ color: peachColor }}>People</span>
        </span>
      )}
    </div>
  );
}

export default KaziPeopleLogo;
